<template>
  <div class="missing-translations">
    <div class="toolbar">
      <select v-model="selectedLocale" @change="loadMissing" class="locale-select">
        <option value="fr">French (FR)</option>
      </select>

      <button
        @click="generateAll"
        :disabled="loading || generating || missingKeys.length === 0"
        class="btn-primary"
      >
        {{ generating ? $t('bo.translations.missing.generating') : $t('bo.translations.missing.generateAll', { count: missingKeys.length }) }}
      </button>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      {{ $t('bo.common.loading') }}
    </div>

    <div v-else-if="missingKeys.length === 0" class="empty">
      {{ $t('bo.translations.missing.allUpToDate', { lang: selectedLocale.toUpperCase() }) }}
    </div>

    <div v-else class="missing-list">
      <div v-for="key in missingKeys" :key="key.id" class="missing-item">
        <div class="missing-info">
          <div class="key-name">{{ key.key }}</div>
          <div class="en-value">EN: {{ getEnValue(key) }}</div>
        </div>

        <div class="missing-actions">
          <textarea
            v-model="suggestions[key.id]"
            rows="2"
            :placeholder="$t('bo.translations.missing.translationPlaceholder', { lang: selectedLocale.toUpperCase() })"
            class="suggestion-input"
          ></textarea>

          <button
            @click="generateOne(key)"
            :disabled="generatingKeys[key.id]"
            class="btn-icon"
            title="Auto-generate this translation"
          >
            {{ generatingKeys[key.id] ? '⏳' : '🤖' }}
          </button>

          <button
            @click="saveSuggestion(key)"
            :disabled="!suggestions[key.id] || savingKeys[key.id]"
            class="btn-icon"
            title="Save this translation"
          >
            {{ savingKeys[key.id] ? '⏳' : '💾' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import translationService from '@/services/translationService'

const { t } = useI18n()

const selectedLocale = ref('fr')
const missingKeys = ref([])
const suggestions = reactive({})
const generatingKeys = reactive({})
const savingKeys = reactive({})
const loading = ref(false)
const generating = ref(false)
const error = ref('')

const getEnValue = (key) => {
  return key.translations?.find(t => t.locale?.code === 'en')?.value || '(no EN text)'
}

const loadMissing = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await translationService.getKeys({ missing_locale: selectedLocale.value })
    missingKeys.value = data.data || []
  } catch (err) {
    error.value = t('bo.translations.missing.errorLoading')
  } finally {
    loading.value = false
  }
}

const generateOne = async (key) => {
  generatingKeys[key.id] = true
  error.value = ''
  try {
    const sourceText = getEnValue(key)
    if (sourceText === '(no EN text)') { error.value = t('bo.translations.missing.noEnText'); return }

    const result = await translationService.autoTranslate({
      items: [{ key_id: key.id, source_text: sourceText }],
      source_locale: 'en',
      target_locale: selectedLocale.value
    })
    const suggestion = result.suggestions?.find(s => s.key_id === key.id)
    if (suggestion) suggestions[key.id] = suggestion.translated_text
  } catch (err) {
    error.value = 'Generation error: ' + (err.response?.data?.message || err.message)
  } finally {
    generatingKeys[key.id] = false
  }
}

const generateAll = async () => {
  generating.value = true
  error.value = ''
  try {
    const items = missingKeys.value
      .map(key => ({ key_id: key.id, source_text: getEnValue(key) }))
      .filter(item => item.source_text !== '(no EN text)')

    if (!items.length) { error.value = 'No keys with EN text to translate'; return }

    const result = await translationService.autoTranslate({
      items,
      source_locale: 'en',
      target_locale: selectedLocale.value
    })
    result.suggestions?.forEach(s => { suggestions[s.key_id] = s.translated_text })
  } catch (err) {
    error.value = 'Batch generation error: ' + (err.response?.data?.message || err.message)
  } finally {
    generating.value = false
  }
}

const saveSuggestion = async (key) => {
  savingKeys[key.id] = true
  error.value = ''
  try {
    await translationService.updateTranslations(key.id, {
      values: [{ locale_code: selectedLocale.value, value: suggestions[key.id] }]
    })
    missingKeys.value = missingKeys.value.filter(k => k.id !== key.id)
    delete suggestions[key.id]
  } catch (err) {
    error.value = 'Save error: ' + (err.response?.data?.message || err.message)
  } finally {
    savingKeys[key.id] = false
  }
}

onMounted(() => loadMissing())
</script>

<style scoped>
.missing-translations {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.locale-select {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.loading {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty {
  padding: 2rem;
  text-align: center;
  background: #f0fdf4;
  color: #166534;
  border-radius: 0.5rem;
  font-weight: 500;
}

.missing-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.missing-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
}

.missing-info { margin-bottom: 0.75rem; }

.key-name {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.en-value {
  color: #6b7280;
  font-size: 0.8rem;
}

.missing-actions {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.suggestion-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  resize: vertical;
  font-size: 0.875rem;
  min-width: 0;
}

.suggestion-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-icon {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 1.1rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}

.btn-icon:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #3b82f6;
}

.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }

.error-message {
  padding: 0.75rem;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 0.375rem;
  margin-top: 1rem;
  font-size: 0.875rem;
}
</style>